/**
 * /fishing-forecast — page controller. The arithmetic lives in engine.js
 * (bite index), species.js (what each fish needs), outdoor-weather/* (the
 * weather record and the place) and ice-thickness/model.js (water temperature
 * and ice, calibrated on open measurements). This file only wires them to the
 * page and draws.
 */
import { SPECIES, DEFAULTS, ORDER } from './species.js';
import { computeIndex, bestWindows, grade, drivers } from './engine.js';
import { fetchConditions, forecastHours, dailyAir, pressureHours, at, localDay } from '../outdoor-weather/series.js';
import { CLASSES, CALIBRATION as WATER_CAL } from '../outdoor-weather/water.js';
import { simulate } from '../ice-thickness/model.js';
import { fromUrl, saved, fromProfile, fillTz, remember, mountPicker, geolocate, offsetHours, hhmm } from '../outdoor-weather/place.js';
import { openDiary } from './diary.js';

const SLUG = 'fishing-forecast';
const CFG = window.__FF || { T: {}, li: {}, cfg: {} };
const T = CFG.T || {};
const HOUR = 3600000, DAY = 86400000;
const COLORS = ['var(--lg-accent)', '#f0a040', '#3fc79a'];
const MAX_SPECIES = 3;

const $ = (s) => document.querySelector(s);
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
function t(key, vars) {
    let s = T[key] != null ? String(T[key]) : key;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(':' + k).join(String(v));
    return s;
}
const locale = () => (window.__intlLocale ? window.__intlLocale() : (CFG.cfg.locale || 'en'));
const nf = (d) => new Intl.NumberFormat(locale(), { maximumFractionDigits: d, minimumFractionDigits: d });

const state = {
    loc: null, water: 'lake', species: [], units: { temp: 'c', press: 'hpa', wind: 'ms' },
    data: null, derived: null, day: 0, hour: null, detailSp: null, loading: false,
};

/* ── preferences ───────────────────────────────────────────────────────── */
function loadPrefs() {
    let p = null;
    try { p = window.__toolSettings && window.__toolSettings.load(SLUG, 'prefs', null); } catch (_) { /* none */ }
    const lang = (CFG.cfg.locale || 'en').slice(0, 2);
    const imperial = (() => { try { return window.__toolSettings && window.__toolSettings.units() === 'imperial'; } catch (_) { return false; } })();
    state.water = p && CLASSES[p.water] ? p.water : 'lake';
    const def = (DEFAULTS[lang] || DEFAULTS.en).slice(0, 2);
    state.species = p && Array.isArray(p.species) ? p.species.filter((k) => SPECIES[k]).slice(0, MAX_SPECIES) : def;
    if (!state.species.length) state.species = def;
    state.units.temp = p && p.temp ? p.temp : (imperial ? 'f' : 'c');
    state.units.press = p && p.press ? p.press : (lang === 'ru' || lang === 'uk' ? 'mmhg' : imperial ? 'inhg' : 'hpa');
    state.units.wind = imperial ? 'mph' : 'ms';
}
function savePrefs() {
    try {
        window.__toolSettings.save(SLUG, 'prefs', { water: state.water, species: state.species, temp: state.units.temp, press: state.units.press });
    } catch (_) { /* private mode */ }
}

/* ── units ─────────────────────────────────────────────────────────────── */
function fmtTemp(c, digits = 1) {
    if (!Number.isFinite(c)) return '—';
    return state.units.temp === 'f' ? nf(digits).format(c * 9 / 5 + 32) + ' °F' : nf(digits).format(c) + ' °C';
}
function fmtTempDelta(c) {
    return state.units.temp === 'f' ? nf(1).format(c * 9 / 5) + ' °F' : nf(1).format(c) + ' °C';
}
function pressValue(hpa) {
    if (state.units.press === 'mmhg') return hpa * 0.750062;
    if (state.units.press === 'inhg') return hpa * 0.0295300;
    return hpa;
}
function fmtPress(hpa) {
    if (!Number.isFinite(hpa)) return '—';
    const d = state.units.press === 'inhg' ? 2 : 0;
    return nf(d).format(pressValue(hpa)) + ' ' + t('u_' + state.units.press);
}
function fmtPressDelta(hpa) {
    if (!Number.isFinite(hpa)) return '—';
    const v = pressValue(hpa), d = state.units.press === 'inhg' ? 2 : 1;
    return (v > 0 ? '+' : v < 0 ? '−' : '') + nf(d).format(Math.abs(v)) + ' ' + t('u_' + state.units.press);
}
function fmtWind(ms) {
    if (!Number.isFinite(ms)) return '—';
    return state.units.wind === 'mph' ? nf(0).format(ms * 2.23694) + ' ' + t('u_mph') : nf(0).format(ms) + ' ' + t('u_ms');
}
const spName = (k) => t('sp_' + k);

/* ── status ────────────────────────────────────────────────────────────── */
function status(kind, text) {
    const el = $('#ffStatus');
    el.hidden = !kind;
    el.className = 'tc-msg ff-status' + (kind === 'error' ? ' bad' : '') + (kind === 'loading' ? ' ff-loading' : '');
    el.textContent = text || '';
}

/* ── place ─────────────────────────────────────────────────────────────── */
function setPlaceLine() {
    const el = $('#ffPlace');
    if (!state.loc) { el.textContent = t('place_none'); return; }
    el.textContent = state.loc.name || (nf(2).format(state.loc.lat) + ', ' + nf(2).format(state.loc.lng));
}
function togglePicker(open) {
    const w = $('#ffPickerWrap');
    w.hidden = !open;
    $('#ffChange').setAttribute('aria-expanded', String(open));
    if (open && !w.dataset.mounted) {
        w.dataset.mounted = mountPicker($('#ffPicker'), SLUG, CFG.li, state.loc, (loc) => { togglePicker(false); load(loc); }) ? '1' : '';
    }
}

/* ── load + compute ────────────────────────────────────────────────────── */
async function load(loc) {
    if (state.loading) return;
    state.loading = true;
    state.loc = await fillTz(loc);
    setPlaceLine();
    status('loading', t('loading'));
    $('#ffBody').hidden = true;
    if (window.__stage) window.__stage('fetch');
    try {
        const data = await fetchConditions(state.loc.lat, state.loc.lng);
        if (!data.fc || data.fc.err || !Array.isArray(data.fc.hours) || !data.fc.hours.length) throw new Error('forecast:' + (data.fc && data.fc.err));
        state.data = data;
        state.day = 0; state.hour = null;
        recompute();
        status(null);
        $('#ffBody').hidden = false;
        render();
        remember(SLUG, state.loc);
        if (window.__announce) window.__announce(t('sr_loaded', { place: state.loc.name || '' }));
        if (window.__stage) window.__stage('shown');
        if (window.__aeToolComplete) {
            window.__aeToolComplete({ sp: state.species.join('+'), obs: state.derived.press.observed ? 1 : 0, hist: data.cond && data.cond.daily ? 1 : 0 });
        }
    } catch (e) {
        status('error', t('err_load'));
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'fetch_failed', detail: String(e && e.message || e).slice(0, 120) });
    } finally {
        state.loading = false;
    }
}

function recompute() {
    const { cond, fc } = state.data;
    const offsetH = offsetHours(state.loc.tz, state.loc.lng);
    const fcRows = forecastHours(fc);
    const days = dailyAir(cond, fcRows, offsetH);
    // Without the past weeks the water model would spin up from the forecast
    // alone and print a confident number it does not have: neutral instead.
    const hasHist = !!(cond && cond.daily && Array.isArray(cond.daily.t) && cond.daily.t.length > 14);
    const sim = simulate(days, CLASSES[state.water]);
    const water = hasHist
        ? sim.map((s) => ({ tw: s.tw, frozen: s.frozen, thaw: s.thaw }))
        : sim.map(() => ({ tw: NaN, frozen: false, thaw: 0 }));
    const press = pressureHours(cond, fcRows);
    const now = Date.now();
    const hours = fcRows.filter((r) => r.t >= Math.floor(now / HOUR) * HOUR - HOUR && r.t < now + 7 * DAY);
    const idx = computeIndex({
        hours, press, days, water, lat: state.loc.lat, lon: state.loc.lng, offsetH, astro: window.AstroCore,
    }, state.species, at);
    // Local days present in the forecast, up to seven.
    const dayKeys = [];
    for (const r of hours) {
        const d = localDay(r.t, offsetH);
        if (!dayKeys.includes(d)) dayKeys.push(d);
    }
    state.derived = { offsetH, fcRows, days, water, press, idx, hasHist, dayKeys: dayKeys.slice(0, 7), dayIdx: new Map(days.map((d, i) => [d.d, i])) };
    if (state.day >= state.derived.dayKeys.length) state.day = 0;
}

function rowsOfDay(sp, dKey) {
    const D = state.derived;
    return (D.idx[sp] || []).filter((r) => localDay(r.t, D.offsetH) === dKey);
}

/* ── render ────────────────────────────────────────────────────────────── */
function render() {
    renderSpecies();
    renderDays();
    renderBest();
    renderChart();
    renderHour();
    renderConditions();
}

function renderSpecies() {
    const host = $('#ffSpecies');
    const lang = (CFG.cfg.locale || 'en').slice(0, 2);
    const shown = host.dataset.all === '1' ? ORDER : Array.from(new Set([...(DEFAULTS[lang] || DEFAULTS.en), ...state.species]));
    host.innerHTML = shown.map((k) => {
        const on = state.species.includes(k);
        const ci = state.species.indexOf(k);
        return '<button type="button" class="tc-chip ff-chip' + (on ? ' on' : '') + '" data-sp="' + k + '" aria-pressed="' + on + '">'
            + (on ? '<i class="ff-dot" style="background:' + COLORS[ci] + '"></i>' : '') + esc(spName(k)) + '</button>';
    }).join('');
    $('#ffMore').hidden = host.dataset.all === '1';
}

function dayLabel(dKey) {
    const d = new Date(dKey + 'T12:00:00Z');
    const wd = new Intl.DateTimeFormat(locale(), { weekday: 'short', timeZone: 'UTC' }).format(d);
    const dm = new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d);
    return { wd, dm };
}

function renderDays() {
    const D = state.derived;
    $('#ffDays').innerHTML = D.dayKeys.map((dKey, i) => {
        const { wd, dm } = dayLabel(dKey);
        const bars = state.species.map((sp, k) => {
            const rows = rowsOfDay(sp, dKey);
            const max = rows.length ? Math.max(...rows.map((r) => r.idx)) : 0;
            return '<span class="ff-mini"><i style="height:' + Math.max(4, max) + '%;background:' + COLORS[k] + '"></i></span>';
        }).join('');
        return '<button type="button" class="ff-day' + (i === state.day ? ' on' : '') + '" data-day="' + i + '" aria-pressed="' + (i === state.day) + '">'
            + '<span class="ff-day-w">' + esc(i === 0 ? t('today') : wd) + '</span><span class="ff-day-d">' + esc(dm) + '</span>'
            + '<span class="ff-minis" aria-hidden="true">' + bars + '</span></button>';
    }).join('');
}

function renderBest() {
    const D = state.derived;
    const dKey = D.dayKeys[state.day];
    const lines = state.species.map((sp, k) => {
        const rows = rowsOfDay(sp, dKey);
        if (!rows.length) return '';
        const wins = bestWindows(rows);
        const max = Math.max(...rows.map((r) => r.idx));
        const g = grade(max);
        const when = wins.length
            ? '<div class="ff-best-t">' + wins.map((w) => esc(hhmm(w.start, D.offsetH) + '–' + hhmm(w.end, D.offsetH))).join(', ') + '</div>'
            : '<div class="ff-best-none">' + esc(t('best_none', { time: hhmm(rows.find((r) => r.idx === max).t, D.offsetH) })) + '</div>';
        const top = rows.find((r) => r.idx === max);
        const why = top ? whyLine(top) : '';
        return '<div class="ff-best-row"><i class="ff-dot" style="background:' + COLORS[k] + '"></i>'
            + '<div class="ff-best-main"><div class="ff-best-h"><b>' + esc(spName(sp)) + '</b>'
            + '<span class="ff-grade g-' + g + '">' + esc(max + ' · ' + t('grade_' + g)) + '</span></div>'
            + when
            + (why ? '<div class="ff-best-why">' + why + '</div>' : '') + '</div></div>';
    }).join('');
    $('#ffBest').innerHTML = lines || '<p class="tc-hint">' + esc(t('no_hours')) + '</p>';
}

/** One sentence from the strongest factors of an hour. */
function whyLine(row) {
    const d = drivers(row);
    const part = (arr, sign) => arr.map(([k, p]) => esc(factorWord(k, row)) + ' ' + (sign > 0 ? '+' : '−') + Math.abs(p) + ' %').join(', ');
    const up = part(d.up, 1), down = part(d.down, -1);
    if (!up && !down) return esc(t('why_flat'));
    return [up && esc(t('why_up')) + ' ' + up, down && esc(t('why_down')) + ' ' + down].filter(Boolean).join(' · ');
}

function factorWord(k, row) {
    const f = row.f;
    switch (k) {
        case 'water': return f.water.frozen ? t('f_water_ice') : t('f_water', { t: fmtTemp(f.water.tw) });
        case 'season': return t('ph_' + f.season.phase);
        case 'light': return t('lt_' + f.light.kind) + (f.light.overcast ? ', ' + t('lt_overcast') : '');
        case 'pressure': return t('pt_' + f.pressure.trend);
        case 'wind': return t('wd_' + f.wind.kind);
        case 'rain': return t('rn_' + f.rain.kind);
        case 'moon': return f.moon.window ? t('mn_' + f.moon.window.what) : (f.moon.newOrFull ? t('mn_phase') : t('mn_none'));
        default: return k;
    }
}

/* ── chart ─────────────────────────────────────────────────────────────── */
function renderChart() {
    const D = state.derived;
    const dKey = D.dayKeys[state.day];
    // Drawn at the container's own width so 12 px text stays 12 px on a phone.
    const W = Math.max(300, Math.round($('#ffChart').clientWidth || 720)), H = 230, L = 34, R = 10, TOP = 22, B = 26;
    const x0 = Date.parse(dKey + 'T00:00:00Z') - D.offsetH * HOUR;
    const xs = (ms) => L + (ms - x0) / DAY * (W - L - R);
    const ys = (v) => TOP + (100 - v) / 100 * (H - TOP - B);
    const parts = [];
    // Night and twilight as bands — when each species' hours fall in the day.
    const A = window.AstroCore;
    for (let h = 0; h < 24; h++) {
        const mid = x0 + h * HOUR + HOUR / 2;
        const alt = A ? A.sunPos(new Date(mid), state.loc.lat, state.loc.lng).altitude * 180 / Math.PI : 10;
        const cls = alt < -6 ? 'ff-night' : alt < 6 ? 'ff-twi' : '';
        if (cls) parts.push('<rect class="' + cls + '" x="' + xs(x0 + h * HOUR).toFixed(1) + '" y="' + TOP + '" width="' + ((W - L - R) / 24 + 0.5).toFixed(1) + '" height="' + (H - TOP - B) + '"/>');
    }
    for (const v of [0, 25, 50, 75, 100]) {
        parts.push('<line class="ff-grid" x1="' + L + '" x2="' + (W - R) + '" y1="' + ys(v).toFixed(1) + '" y2="' + ys(v).toFixed(1) + '"/>');
        parts.push('<text class="ff-yl" x="' + (L - 6) + '" y="' + (ys(v) + 4).toFixed(1) + '">' + v + '</text>');
    }
    for (let h = 0; h <= 24; h += W < 520 ? 6 : 3) {
        parts.push('<text class="ff-xl" x="' + xs(x0 + h * HOUR).toFixed(1) + '" y="' + (H - 8) + '">' + String(h % 24).padStart(2, '0') + ':00</text>');
    }
    // Moon windows (from the first species' rows — the same for all).
    const first = rowsOfDay(state.species[0], dKey);
    const seen = new Set();
    for (const r of first) {
        const w = r.f.moon.window;
        if (!w || seen.has(w.t)) continue;
        seen.add(w.t);
        const cx = xs(w.t);
        if (cx < L || cx > W - R) continue;
        parts.push('<g class="ff-moon"><circle cx="' + cx.toFixed(1) + '" cy="12" r="' + (w.kind === 'major' ? 5 : 4) + '" class="' + (w.kind === 'major' ? 'maj' : 'min') + '"/>'
            + '<title>' + esc(t('mn_' + w.what) + ' ' + hhmm(w.t, D.offsetH)) + '</title></g>');
    }
    // Lines.
    state.species.forEach((sp, k) => {
        const rows = rowsOfDay(sp, dKey);
        if (!rows.length) return;
        const d = rows.map((r, i) => (i ? 'L' : 'M') + xs(r.t + HOUR / 2).toFixed(1) + ' ' + ys(r.idx).toFixed(1)).join(' ');
        parts.push('<path class="ff-line" style="stroke:' + COLORS[k] + '" d="' + d + '"/>');
    });
    // Now.
    const now = Date.now();
    if (now >= x0 && now < x0 + DAY) {
        parts.push('<line class="ff-now" x1="' + xs(now).toFixed(1) + '" x2="' + xs(now).toFixed(1) + '" y1="' + TOP + '" y2="' + (H - B) + '"/>');
    }
    // Hit areas.
    for (const r of first) {
        parts.push('<rect class="ff-hit' + (state.hour === r.t ? ' on' : '') + '" data-h="' + r.t + '" x="' + xs(r.t).toFixed(1) + '" y="' + TOP + '" width="' + ((W - L - R) / 24).toFixed(1) + '" height="' + (H - TOP - B) + '"><title>' + esc(hhmm(r.t, D.offsetH)) + '</title></rect>');
    }
    $('#ffChart').innerHTML = '<svg viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="' + esc(t('chart_label')) + '">' + parts.join('') + '</svg>';
    $('#ffCoarse').hidden = !first.some((r) => r.coarse);
}

function renderHour() {
    const box = $('#ffHour');
    const D = state.derived;
    if (state.hour === null) { box.hidden = true; box.innerHTML = ''; return; }
    const sp = state.detailSp && state.species.includes(state.detailSp) ? state.detailSp : state.species[0];
    const row = (D.idx[sp] || []).find((r) => r.t === state.hour);
    if (!row) { box.hidden = true; return; }
    const tabs = state.species.map((k, i) => '<button type="button" class="tc-seg-b' + (k === sp ? ' on' : '') + '" data-dsp="' + k + '"><i class="ff-dot" style="background:' + COLORS[i] + '"></i>'
        + esc(spName(k)) + ' ' + ((D.idx[k] || []).find((r) => r.t === state.hour) || { idx: '—' }).idx + '</button>').join('');
    const f = row.f;
    const pct = (v) => { const p = Math.round((v - 1) * 100); return (p > 0 ? '+' : p < 0 ? '−' : '±') + Math.abs(p) + ' %'; };
    const lines = [
        [t('f_h_water'), f.water.frozen ? t('f_water_ice') : fmtTemp(f.water.tw) + ' · ' + t('water_est'), f.water.f],
        [t('f_h_season'), t('ph_' + f.season.phase), f.season.f],
        [t('f_h_light'), t('lt_' + f.light.kind) + (f.light.overcast ? ', ' + t('lt_overcast') : ''), f.light.f],
        [t('f_h_pressure'), t('pt_' + f.pressure.trend) + (Number.isFinite(f.pressure.d72) ? ' · ' + t('f_72h', { v: fmtPressDelta(f.pressure.d72) }) : ''), f.pressure.f],
        [t('f_h_wind'), fmtWind(f.wind.v) + ' · ' + t('wd_' + f.wind.kind), f.wind.f],
        [t('f_h_rain'), t('rn_' + f.rain.kind), f.rain.f],
        [t('f_h_moon'), f.moon.window ? t('mn_' + f.moon.window.what) + ' ' + hhmm(f.moon.window.t, D.offsetH) : (f.moon.newOrFull ? t('mn_phase') : t('mn_none')), f.moon.f],
    ];
    box.hidden = false;
    box.innerHTML = '<div class="ff-hour-h"><b>' + esc(hhmm(state.hour, D.offsetH) + '–' + hhmm(state.hour + HOUR, D.offsetH)) + '</b>'
        + '<button type="button" class="ff-x" data-hclose aria-label="' + esc(t('close')) + '">×</button></div>'
        + '<div class="tc-seg ff-dsp">' + tabs + '</div>'
        + '<table class="tc-table ff-ftable"><tbody>'
        + lines.map(([a, b, v]) => '<tr><td>' + esc(a) + '</td><td>' + esc(b) + '</td><td class="num ' + (v >= 1.04 ? 'up' : v <= 0.92 ? 'down' : '') + '">' + pct(v) + '</td></tr>').join('')
        + '</tbody></table>'
        + '<p class="tc-note">' + esc(t('f_formula', { idx: row.idx })) + '</p>';
}

function renderConditions() {
    const D = state.derived;
    const now = Date.now();
    const today = localDay(now, D.offsetH);
    const wi = D.dayIdx.get(today);
    const w = wi !== undefined ? D.water[wi] : null;
    const p0 = at(D.press.rows, Math.floor(now / HOUR) * HOUR);
    const p72 = at(D.press.rows, Math.floor(now / HOUR) * HOUR - 72 * HOUR);
    const fc0 = D.fcRows.find((r) => r.t >= now - HOUR) || D.fcRows[0];
    const ill = window.AstroCore ? window.AstroCore.moonIllum(new Date(now)) : null;
    const phaseName = ill ? moonPhaseName(ill.phase) : '';
    const tiles = [
        !D.hasHist
            ? [t('c_water'), '—', t('c_water_nohist')]
            : [t('c_water'), w ? (w.frozen ? t('c_ice') : '≈ ' + fmtTemp(w.tw)) : '—', w && !w.frozen ? t('c_water_err', { err: fmtTempDelta(WATER_CAL.maeC) }) : ''],
        [t('c_pressure'), fmtPress(p0), Number.isFinite(p0) && Number.isFinite(p72) ? t('f_72h', { v: fmtPressDelta(p0 - p72) }) : t('c_no_trend')],
        [t('c_wind'), fc0 ? fmtWind(fc0.wind) : '—', fc0 && Number.isFinite(fc0.dir) ? t('dir_' + dirKey(fc0.dir)) : ''],
        [t('c_moon'), phaseName ? t(phaseName) : '—', ill ? t('c_moon_lit', { p: Math.round(ill.fraction * 100) }) : ''],
    ];
    $('#ffCond').innerHTML = tiles.map(([l, v, s]) => '<div class="tc-stat"><div class="tc-stat-l">' + esc(l) + '</div><div class="tc-stat-v">' + esc(v) + '</div>'
        + (s ? '<div class="tc-stat-s">' + esc(s) + '</div>' : '') + '</div>').join('');
    const st = D.press.station;
    $('#ffSource').textContent = D.press.observed && st
        ? t('src_station', { name: st.name || st.id, km: nf(0).format(st.km || 0) })
        : t('src_no_station');
}

function moonPhaseName(phase) {
    const k = Math.round(phase * 8) % 8;
    return ['moon_new', 'moon_waxing_crescent', 'moon_first_quarter', 'moon_waxing_gibbous', 'moon_full', 'moon_waning_gibbous', 'moon_last_quarter', 'moon_waning_crescent'][k];
}
function dirKey(deg) {
    return ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'][Math.round(((deg % 360) + 360) % 360 / 45) % 8];
}

/* ── events ────────────────────────────────────────────────────────────── */
function bind() {
    $('#ffChange').addEventListener('click', () => togglePicker($('#ffPickerWrap').hidden));
    $('#ffGeo').addEventListener('click', () => geolocate($('#ffGeo'), (loc) => { togglePicker(false); load(loc); }));

    $('#ffWater').addEventListener('click', (e) => {
        const b = e.target.closest('[data-water]');
        if (!b || b.dataset.water === state.water) return;
        state.water = b.dataset.water;
        syncWater();
        savePrefs();
        if (state.data) { recompute(); render(); }
    });

    $('#ffSpecies').addEventListener('click', (e) => {
        const b = e.target.closest('[data-sp]');
        if (!b) return;
        const k = b.dataset.sp;
        if (state.species.includes(k)) {
            if (state.species.length === 1) return;
            state.species = state.species.filter((x) => x !== k);
        } else {
            if (state.species.length >= MAX_SPECIES) state.species.shift();
            state.species.push(k);
        }
        savePrefs();
        if (state.data) { recompute(); render(); } else renderSpecies();
    });
    $('#ffMore').addEventListener('click', () => { $('#ffSpecies').dataset.all = '1'; renderSpecies(); });

    $('#ffDays').addEventListener('click', (e) => {
        const b = e.target.closest('[data-day]');
        if (!b) return;
        state.day = +b.dataset.day; state.hour = null;
        renderDays(); renderBest(); renderChart(); renderHour();
    });
    $('#ffChart').addEventListener('click', (e) => {
        const r = e.target.closest('[data-h]');
        if (!r) return;
        const h = +r.dataset.h;
        state.hour = state.hour === h ? null : h;
        renderChart(); renderHour();
    });
    $('#ffHour').addEventListener('click', (e) => {
        if (e.target.closest('[data-hclose]')) { state.hour = null; renderChart(); renderHour(); return; }
        const b = e.target.closest('[data-dsp]');
        if (b) { state.detailSp = b.dataset.dsp; renderHour(); }
    });

    for (const [id, key] of [['#ffTempU', 'temp'], ['#ffPressU', 'press']]) {
        const sel = $(id);
        sel.value = state.units[key];
        sel.addEventListener('change', () => {
            state.units[key] = sel.value;
            savePrefs();
            if (state.data) render();
        });
    }
}

function syncWater() {
    $('#ffWater').querySelectorAll('[data-water]').forEach((b) => {
        const on = b.dataset.water === state.water;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', String(on));
    });
    $('#ffWaterHint').textContent = t('water_hint_' + state.water);
}

/* ── boot ──────────────────────────────────────────────────────────────── */
function watchWidth() {
    if (!('ResizeObserver' in window)) return;
    let last = 0, timer = 0;
    new ResizeObserver((es) => {
        const w = Math.round(es[0].contentRect.width);
        if (!w || Math.abs(w - last) < 8) return;
        last = w;
        clearTimeout(timer);
        timer = setTimeout(() => { if (state.derived) renderChart(); }, 120);
    }).observe($('#ffChart'));
}

function boot() {
    loadPrefs();
    watchWidth();
    syncWater();
    renderSpecies();
    bind();
    openDiary({
        root: $('#ffDiary'),
        t, esc, nf, spName,
        species: () => state.species,
        allSpecies: ORDER,
        indexAt: (sp, ms) => {
            const D = state.derived;
            if (!D || !D.idx[sp]) return null;
            const h = Math.floor(ms / HOUR) * HOUR;
            const r = D.idx[sp].find((x) => x.t === h);
            return r || null;
        },
        place: () => state.loc,
    });
    window.__FF_APP = { state, reload: () => state.loc && load(state.loc) };
    const loc = fromUrl() || saved(SLUG) || fromProfile();
    if (loc) load(loc);
    else { status('empty', t('empty')); togglePicker(true); }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
